import React, { useContext, useMemo } from 'react';
import styled, { ThemeContext } from 'styled-components';
import { useFilterOptionsContext } from './useFilterOptionsContext';
import {
  Table,
  TableWrapper,
  TableHeaders,
  TableHeading,
  TableRow,
  Cell,
} from '../metrics-table/table';
import { Wrapper } from '../campaigns-components/campaigns-list.styles';
import { formatNumbersWithUnits } from '../../utils/formatNumbers';
import { getMetricChangeValueColorAndIcon } from '../../utils/widgets-ui';
import { MetricChangeValue, StyledIcon } from '../widgets-ui/kpi';
import WidgetHeader from '../widgets-ui/common/widget-header';
import { breakpoints } from '../../styles/variables';
import { LoadingSpinnerInline } from '../heatmap-page-components/styled';
import { WidgetTooltip } from '../widgets-ui/common/widget-tooltip';
import { Tooltip2 } from '@blueprintjs/popover2';
import { Icon, Position } from '@blueprintjs/core';
import MetricChangeComponent from './metric-change-component';

const CampaignTableFooter = styled.div`
  padding: 0.5rem;
  border-bottom: var(--widget-divider);
  background-color: var(--white);
  border-radius: 0 0 var(--widget-border-radius) var(--widget-border-radius);

  @media (max-width: ${breakpoints.TABLET_SIZE}) {
    padding: 1.5rem 1.5rem;
  }
`;

const TooltipStyled = styled(Tooltip2)`
  margin-left: 0.5rem;
`;

const CampaignTable = () => {
  const { state } = useFilterOptionsContext();
  const { data, filterOptions, dataUplift } = state;

  const unitType = useMemo(
    () => filterOptions.metric.unitType || 'number',
    [filterOptions.metric.unitType],
  );

  const hasControlLocations =
    filterOptions.controlLocations && filterOptions.controlLocations.length > 0;
  const hasControlPeriod =
    filterOptions.controlPeriod && filterOptions.controlPeriod.active;

  return (
    <>
      <Wrapper>
        <WidgetHeader
          title={filterOptions.name || 'New Campaign'}
          subtitle={`Comparing the average ${
            filterOptions.metric.display
          } per location, per day,
            between ${filterOptions.testLocations.length} test locations${
            hasControlLocations
              ? `, ${filterOptions.controlLocations.length} control locations`
              : ''
          }${
            hasControlPeriod
              ? `, and between test and control date periods`
              : ''
          }.`}
          // description='Uplift is calculated as the difference between the change in average value per location, per day, of the test and location sets, compared between the test and control date periods.'
        />

        <TableWrapper>
          <Table>
            <TableHeaders>
              <tr>
                <TableHeading></TableHeading>
                <TableHeading>
                  Test Period
                  <TooltipStyled
                    content={`Average ${filterOptions.metric.display} per location, per day, during the test period.`}
                    position={Position.TOP}
                  >
                    <Icon icon="help" iconSize={15} />
                  </TooltipStyled>
                </TableHeading>
                {hasControlPeriod && (
                  <>
                    <TableHeading>
                      Control Period
                      <TooltipStyled
                        content={`Average ${filterOptions.metric.display} per location, per day, during the control period.`}
                        position={Position.TOP}
                      >
                        <Icon icon="help" iconSize={15} />
                      </TooltipStyled>
                    </TableHeading>
                    <TableHeading>
                      {!hasControlLocations && 'Campaign '}Uplift
                      <TooltipStyled
                        content={`Percentage change between the test period average and the control period average.`}
                        position={Position.TOP}
                      >
                        <Icon icon="help" iconSize={15} />
                      </TooltipStyled>
                    </TableHeading>
                  </>
                )}
              </tr>
            </TableHeaders>
            <tbody>
              <TableRow>
                <Cell cellType={'string'}>
                  Test Locations ({filterOptions.testLocations.length})
                  <TooltipStyled
                    content={`Average ${filterOptions.metric.display} per location, per day, for the test locations.`}
                    position={Position.TOP}
                  >
                    <Icon icon="help" iconSize={15} />
                  </TooltipStyled>
                </Cell>
                <Cell cellType={'number'} highlight>
                  {formatNumbersWithUnits(data.test_test, unitType)}
                </Cell>
                {hasControlPeriod && (
                  <>
                    <Cell cellType={'number'}>
                      {formatNumbersWithUnits(data.test_control, unitType)}
                    </Cell>
                    <Cell cellType={'number'}>
                      <MetricChangeComponent
                        value={dataUplift.testUplift}
                        unit={unitType}
                      />
                    </Cell>
                  </>
                )}
              </TableRow>
              {hasControlLocations && (
                <>
                  <TableRow>
                    <Cell cellType={'string'}>
                      Control Locations ({filterOptions.controlLocations.length})
                      <TooltipStyled
                        content={`Average ${filterOptions.metric.display} per location, per day, for the control locations.`}
                        position={Position.TOP}
                      >
                        <Icon icon="help" iconSize={15} />
                      </TooltipStyled>
                    </Cell>
                    <Cell cellType={'number'}>
                      {formatNumbersWithUnits(data.control_test, unitType)}
                    </Cell>
                    {hasControlPeriod && (
                      <>
                        <Cell cellType={'number'}>
                          {formatNumbersWithUnits(
                            data.control_control,
                            unitType,
                          )}
                        </Cell>
                        <Cell cellType={'number'}>
                          <MetricChangeComponent
                            value={dataUplift.controlUplift}
                            unit={unitType}
                          />
                        </Cell>
                      </>
                    )}
                  </TableRow>
                </>
              )}
              {hasControlLocations && (
                <>
                  <TableRow>
                    <Cell cellType={'string'}>
                      Campaign Uplift
                      <TooltipStyled
                        content={`Percentage change between the test location uplift and the control location uplift.`}
                        position={Position.TOP}
                      >
                        <Icon icon="help" iconSize={15} />
                      </TooltipStyled>
                    </Cell>
                    {!hasControlPeriod ? (
                      <Cell cellType={'number'}>
                        <MetricChangeComponent
                          value={dataUplift.locationUplift}
                          unit={unitType}
                        />
                      </Cell>
                    ) : (
                      <>
                        <Cell />
                        <Cell />
                        <Cell cellType={'number'}>
                          <MetricChangeComponent
                            value={dataUplift.overallUpliftDelta}
                            unit={unitType}
                          />
                        </Cell>
                      </>
                    )}
                  </TableRow>
                </>
              )}
            </tbody>
          </Table>
        </TableWrapper>
        <CampaignTableFooter />
      </Wrapper>
    </>
  );
};

export default CampaignTable;
